import { useState, useRef, useEffect } from 'react';
import { getTerminal, cancelPaymentIfNeeded } from "../../services/stripeTerminal";
// Processing Loader Component
function ProcessingLoader() {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-32 w-32 border-t-4 border-b-4 border-boonsOrange mb-8"></div>
        <p className="text-white text-4xl font-medium">
          Processing Payment...
        </p>
        <p className="text-xl text-gray-600">Please wait, do not remove your card</p>
      </div>
    </div>
  );
}

export default function PaymentModal({ isOpen, onClose, total, accessToken, restaurantId, InitiatePayment, onPaymentSuccess, onPaymentFailure, onAutoCanel }) {
  const statusWatcherRef = useRef(null);
  const userCancelledRef = useRef(false);
  const timeoutRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isWaitingForCard, setIsWaitingForCard] = useState(false);
  const [error, setError] = useState(null);
  const paymentStartedRef = useRef(false);
  // status watcher
  function startStatusWatcher() {
    const terminal = getTerminal();
    const interval = setInterval(() => {
      const status = terminal.getPaymentStatus();

      if (status === "waiting_for_input") {
        setIsWaitingForCard(true);
        setIsProcessing(false);
      }

      if (status === "processing") {
        setIsWaitingForCard(false);
        setIsProcessing(true);
      }

      if (status === "completed" || status === "failed") {
        clearInterval(interval);
        statusWatcherRef.current = null;
      }
    }, 250);

    return interval;
  }
  useEffect(() => {
    if (!isOpen) return;
    if (paymentStartedRef.current) return;
    userCancelledRef.current = false;

    // auto cancel if card not tapped or modal didnt close 
    timeoutRef.current = setTimeout(async () => {
      console.log("AUTO-CANCEL: No card tapped for 90 seconds");
      userCancelledRef.current = true;
      await cancelPaymentIfNeeded(statusWatcherRef);
      onAutoCanel();
    }, 90000);

    // Start the payment flow automatically when modal opens
    const startPaymentFlow = async () => {
      try {
        setError(null);

        // Call Payment with callbacks
        await InitiatePayment(
          accessToken,
          {
            //simulated: true, // Change to false for real card
            amount: Math.round(total * 100), // Convert to cents
            restaurantId: restaurantId
          },
          {
            // Called when waiting for card tap
            onWaitingForCard: () => {
              if (userCancelledRef.current) return;
              console.log('Waiting for card tap...');
              setIsWaitingForCard(true);
              setIsProcessing(false);
              if (!statusWatcherRef.current) {
                statusWatcherRef.current = startStatusWatcher();
              }
            },

            // Called when card is tapped
            onCardTapped: (paymentIntent) => {
              console.log('Card tapped!', paymentIntent);

              // Keep waiting screen, will switch to processing next
            },

            // Called when processing payment
            onProcessing: () => {
              if (userCancelledRef.current) return;
              console.log('Processing payment...');
              setIsWaitingForCard(false);
              setIsProcessing(true);
              if (!statusWatcherRef.current) {
                statusWatcherRef.current = startStatusWatcher();
              }
            },

            // Called when payment succeeds
            onSuccess: (paymentIntent) => {
              if (userCancelledRef.current) return;
              console.log('Payment successful!', paymentIntent);
              if (statusWatcherRef.current) {
                clearInterval(statusWatcherRef.current);
                statusWatcherRef.current = null;
              }
              onPaymentSuccess(paymentIntent);
              onClose();
              // Show success briefly then close
              setTimeout(() => {
                setIsProcessing(false);
                setIsWaitingForCard(false);
                // onClose();
              }, 1500);

            },

            // Called when payment fails
            onError: (err) => {

              if (userCancelledRef.current) return;
              if (statusWatcherRef.current) {
                clearInterval(statusWatcherRef.current);
                statusWatcherRef.current = null;
              }
              onPaymentFailure(err?.code || "Payment failed");
              //console.log("error", err.code);
              setError(err?.code || "Payment failed");
              setIsWaitingForCard(false);
              setIsProcessing(false);
            }
          }
        );

      } catch (err) {
        console.error('Payment failed:', err);
        setError(err.message);
        setIsWaitingForCard(false);
        setIsProcessing(false);
      }
    };
    paymentStartedRef.current = true;
    startPaymentFlow();

    // Cleanup
    return () => {
      paymentStartedRef.current = false;
      setIsWaitingForCard(false);
      setIsProcessing(false);
      setError(null);
    };
  }, [isOpen, total, accessToken, restaurantId, InitiatePayment, onClose]);

  if (!isOpen) return null;

  // Show processing loader when processing payment
  if (isProcessing) {
    return <ProcessingLoader />;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-80"
        onClick={async () => {
          userCancelledRef.current = true;
          await cancelPaymentIfNeeded(statusWatcherRef);
          onClose();
        }}
      />

      {/* Modal */}
      <div className="relative bg-white max-w-4xl mx-4 overflow-hidden shadow-2xl rounded-2xl">
        {/* Header */}
        <div className="bg-white md:px-6 px-2 md:py-6 py-2 flex items-center justify-between">
          <button
            onClick={async () => {
              userCancelledRef.current = true;
              await cancelPaymentIfNeeded(statusWatcherRef);
              onClose();
            }}
            className="w-12 h-12 flex items-center justify-center text-white rounded-xl transition-colors"
          >
            <img
              src="/images/back.png"
              alt="back"
              className="w-[50px] h-[50px] rounded-[12px]"
            />
          </button>
          <p className="md:text-4xl text-2xl font-outfit font-medium text-textcolor absolute left-1/2 transform -translate-x-1/2">
            Your Order Total
          </p>
        </div>

        {/* Total Amount */}
        <div className="bg-white md:px-6 px-2 md:py-8 py-2 text-center">
          <div className="md:text-8xl text-4xl font-outfit font-medium text-textcolor">
            ${total}
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-paymentbg md:px-6 px-2 md:py-12 py-6 text-center">
          <p className="md:text-[40px] text-2xl font-outfit font-medium text-textcolor mb-6 leading-relaxed">
            Please Insert/Tap Your Card<br />
            To The Right
          </p>

          {/* Animated Arrows */}
          <div className="flex items-center justify-center gap-2">

            {/* Arrow 1 */}
            <img
              src="/images/cardswipeloader.png"
              alt="arrow"
              className="w-10 h-10 animate-pulse"
              style={{ animationDelay: '0s', animationDuration: '1.5s' }}
            />

            {/* Arrow 2 */}
            <img
              src="/images/cardswipeloader.png"
              alt="arrow"
              className="w-10 h-10 animate-pulse"
              style={{ animationDelay: '0s', animationDuration: '1.5s' }}
            />

            {/* Arrow 3 */}
            <img
              src="/images/cardswipeloader.png"
              alt="arrow"
              className="w-10 h-10 animate-pulse"
              style={{ animationDelay: '0s', animationDuration: '1.5s' }}
            />

          </div>

          {/* Status indicator */}
          {isWaitingForCard && (
            <div className="md:mt-6 mt-2 flex items-center justify-center gap-2 text-green-600">
              <div className="w-3 h-3 bg-green-600 rounded-full animate-pulse"></div>
              <span className="md:text-lg text-sm font-medium">Ready - Waiting for card tap...</span>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="md:mt-6 mt-2 md:p-4 p-2 bg-red-100 border border-red-400 rounded-lg">
              <p className="text-red-700 md:text-lg text-sm font-medium">
                Error: {error}
              </p>
              <button
                onClick={onClose}
                className="md:mt-3 mt-2 md:px-6 px-2 md:py-2 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}